import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  Grid,
  Box,
  Collapse,
  Fade,
  Alert,
  Link,
  Autocomplete,
  CircularProgress,
} from '@mui/material'

const AwsConfigurationStep = ({
  formData,
  setFormData,
  isComplete,
  itsmProducts,
  loadingProducts,
  selectedProduct,
  handleProductChange,
  productSearchText,
  setProductSearchText,
  awsAccounts,
  loadingAccounts,
  handleModalOpenWithProduct,
  stepNumber = 3,
}) => {
  return (
    <Collapse in={formData.isAWS === 'true'}>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, bgcolor: 'blue.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isComplete ? "✓" : String(stepNumber)} 
              color={isComplete ? "success" : "primary"} 
              size="small" 
            />
            AWS Configuration
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          {/* Product Name */}
          {formData.preExisting === 'true' ? (
            <Autocomplete
              fullWidth
              options={itsmProducts}
              getOptionLabel={(option) => option?.product_name || ''}
              value={selectedProduct}
              onChange={handleProductChange}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setProductSearchText(newInputValue)
                }
              }}
              loading={loadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product Name *"
                  placeholder="Search products..."
                  helperText="Auto-populated from Venafi. You can change this if needed."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 3 }}
              isOptionEqualToValue={(option, value) => option.product_code === value.product_code}
            />
          ) : (
            <Autocomplete
              fullWidth
              options={itsmProducts}
              getOptionLabel={(option) => option?.product_name || ''}
              value={selectedProduct}
              onChange={handleProductChange}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setProductSearchText(newInputValue)
                }
              }}
              loading={loadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product Name *"
                  placeholder="Search products..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 3 }}
              isOptionEqualToValue={(option, value) => option.product_code === value.product_code}
            />
          )}

          {/* Loading indicator while accounts are being fetched */}
          {formData.productCode && loadingAccounts && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading AWS accounts for this product…
              </Typography>
            </Box>
          )}

          {/* No onboarded accounts warning */}
          {formData.productCode && !loadingAccounts && awsAccounts.length === 0 && (
            <Fade in={true}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  There are no onboarded AWS accounts for this product yet.
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault()
                    handleModalOpenWithProduct()
                  }}
                  sx={{ 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    '&:hover': { color: 'primary.dark' } 
                  }}
                >
                  Click Here to Onboard an AWS Account for {itsmProducts.find(p => p.product_code === formData.productCode)?.product_name}
                </Link>
              </Alert>
            </Fade>
          )}

          {/* AWS Account Name - Only visible if product has onboarded accounts */}
          {formData.productCode && awsAccounts.length > 0 && (
            <Fade in={true}>
              <Box>
                <Autocomplete
                  fullWidth
                  options={awsAccounts}
                  getOptionLabel={(option) => `${option.account_name} (${option.product_env.toUpperCase()})`}
                  value={awsAccounts.find(a => 
                    a.account_number === formData.awsAdditionalFields.accountNumber && 
                    a.product_env === formData.environment
                  ) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        environment: newValue.product_env,
                        awsAdditionalFields: {
                          ...prev.awsAdditionalFields,
                          accountName: newValue.account_name,
                          accountNumber: newValue.account_number,
                          applicationId: newValue.application_id,
                        },
                      }))
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        environment: '',
                        awsAdditionalFields: {
                          ...prev.awsAdditionalFields,
                          accountName: '',
                          accountNumber: '',
                          applicationId: '',
                        },
                      }))
                    }
                  }}
                  loading={loadingAccounts}
                  disabled={loadingAccounts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AWS Account Name *"
                      placeholder={awsAccounts.length === 0 ? "No onboarded AWS accounts for this product" : "Select account..."}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={`${option.account_number}-${option.product_env}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                        <Typography>{option.account_name}</Typography>
                        <Chip 
                          label={option.product_env.toUpperCase()} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </li>
                  )}
                  sx={{ mb: 3 }}
                  isOptionEqualToValue={(option, value) => 
                    option.account_number === value.account_number && 
                    option.product_env === value.product_env
                  }
                />

                {/* Prompt to onboard another account */}
                {!formData.awsAdditionalFields.accountName && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault()
                        handleModalOpenWithProduct()
                      }}
                      sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Need to onboard another account?
                    </Link>
                  </Box>
                )}
              </Box>
            </Fade>
          )}

          {/* Auto-populated fields - Visible after account selected */}
          {formData.awsAdditionalFields.accountName && (
            <Fade in={true}>
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Product Code"
                      value={formData.productCode}
                      InputProps={{ readOnly: true }}
                      helperText="Product catalog ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="AWS Account Number"
                      value={formData.awsAdditionalFields.accountNumber}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-populated"
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Application ID"
                  value={formData.awsAdditionalFields.applicationId}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 3 }}
                  helperText="Application UUID (brcp:application:id)"
                />

                {/* Environment Display */}
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">SDLC Environment *</FormLabel>
                  <Box sx={{ mt: 1, mb: 1 }}>
                    {formData.environment && (
                      <Chip
                        label={formData.environment.toUpperCase()}
                        color="primary"
                        sx={{ minWidth: 70 }}
                      />
                    )}
                  </Box>
                  <FormHelperText>
                    Environment is set based on selected account
                  </FormHelperText>
                </FormControl>
              </Box>
            </Fade>
          )}
        </CardContent>
      </Card>
    </Collapse>
  )
}

export default AwsConfigurationStep
